import * as React from "react";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { Button, Skeleton } from "@/components/ui";
import { ReviewRating } from "@/components/reviews";
import { formatRelativeTime, SOURCE_SYSTEM_CONFIG } from "@/components/reviews/utils";
import { Review } from "@/services/bedrock/dtos/Review";

export interface ReviewPickerRowProps {
  review: Review;
  index: number;
  pinned: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}

/** Reviewer, source, date, and the quote; used by the picker list and the "add another" search. */
export const ReviewSummaryLine: React.FC<{ review: Review }> = ({ review }) => {
  const source = SOURCE_SYSTEM_CONFIG[review.sourceSystem]?.name ?? review.sourceSystem;
  const quote = review.condensedQuote || review.content;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
        <ReviewRating review={review} size="sm" />
        <span className="font-semibold text-slate-800">{review.displayName}</span>
        <span className="text-xs text-slate-500">
          {source} · {formatRelativeTime(review.effectiveDate ?? review.publishedAt)}
        </span>
      </div>
      {quote && <p className="line-clamp-2 text-sm text-slate-600">“{quote}”</p>}
    </div>
  );
};

const CONTROL = "max-sm:size-10";

export const ReviewPickerRow: React.FC<ReviewPickerRowProps> = ({
  review,
  index,
  pinned,
  canMoveUp,
  canMoveDown,
  onMove,
  onRemove,
}) => (
  <li
    className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3"
    data-testid="review-picker-row"
  >
    <span className="mt-0.5 w-5 shrink-0 text-sm font-semibold text-slate-400">{index + 1}.</span>
    <div className="min-w-0 flex-1">
      <ReviewSummaryLine review={review} />
    </div>
    {pinned ? (
      <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-primary">
        This review
      </span>
    ) : (
      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          size="icon-xs"
          variant="ghost"
          className={CONTROL}
          aria-label="Move up"
          disabled={!canMoveUp}
          onClick={() => onMove(-1)}
        >
          <ArrowUp />
        </Button>
        <Button
          size="icon-xs"
          variant="ghost"
          className={CONTROL}
          aria-label="Move down"
          disabled={!canMoveDown}
          onClick={() => onMove(1)}
        >
          <ArrowDown />
        </Button>
        <Button size="icon-xs" variant="ghost" className={CONTROL} aria-label="Remove" onClick={onRemove}>
          <X />
        </Button>
      </div>
    )}
  </li>
);

/** A review row's shape while reviews load. */
export const ReviewRowSkeleton: React.FC<{ numbered?: boolean }> = ({ numbered = true }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3" aria-hidden="true">
    {numbered && <Skeleton className="mt-0.5 h-4 w-4 shrink-0" />}
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <div className="flex items-center gap-2">
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
      <Skeleton className="h-3 w-[92%]" />
      <Skeleton className="h-3 w-[70%]" />
    </div>
    {numbered && (
      <div className="flex shrink-0 gap-1">
        <Skeleton className="size-6 rounded-md" />
        <Skeleton className="size-6 rounded-md" />
      </div>
    )}
  </div>
);
